import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import QRCode from 'qrcode';
import { Check, ChevronLeft, Plus, Zap, Wifi, ImageIcon, Sparkles, MonitorPlay, Copy, ExternalLink, Eye, Images, Download, FileImage } from 'lucide-react';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Input, Textarea } from '../UI/Input';
import { InviteLinkPanel } from './InviteLinkPanel';
import { OverlayConfigCard } from './OverlayConfigCard';
import { SlideshowSetupModal } from './SlideshowSetupModal';
import { A4PosterModal } from './A4PosterModal';
import { albumsApi } from '../../api/albums';
import { getPortalUrl } from '../../lib/portal';
import { eventsApi } from '../../api/events';
import { ftpAccountsApi } from '../../api/ftpAccounts';
import { overlaysApi } from '../../api/overlays';
import { Album, AlbumOverlayConfig, FTPAccount, Overlay, OverlayAnchor } from '../../types';
import { QuotaLimitModal } from '../UI/QuotaLimitModal';
import { useQuotaGuard } from '../../hooks/useQuotaGuard';
import { brandingApi } from '../../api/settings';
import { getReservedAnchors, pickFreeAnchor } from '../../lib/overlayAnchors';
import { LiveBadge } from '../UI/LiveBadge';
import { useAuthStore } from '../../store/authStore';
import { FaceRecognitionStep, FaceRecognitionConfig } from './FaceRecognitionStep';
import styles from './LiveAlbumWizard.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LiveAlbumWizardProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  onCreated: (album: Album) => void;
}

/** Logical step names — the numeric mapping depends on whether backfill step is shown */
type LogicalStep = 'source' | 'backfill' | 'face' | 'duplicates' | 'overlays' | 'done';

// ─── Step dots ────────────────────────────────────────────────────────────────

const STEP_LABEL_MAP: Record<LogicalStep, string> = {
  source: 'Source',
  backfill: 'Photos',
  face: 'Face',
  duplicates: 'Duplicates',
  overlays: 'Overlays',
  done: 'Done',
};

function StepDots({ currentStep, steps }: { currentStep: LogicalStep; steps: LogicalStep[] }) {
  const currentIdx = steps.indexOf(currentStep);
  // Don't render dots for the done step
  const dotsSteps = steps.filter((s) => s !== 'done');

  return (
    <div className={styles['step-dots']}>
      {dotsSteps.map((s, i) => {
        const stepIdx = steps.indexOf(s);
        const done = currentIdx > stepIdx;
        const active = currentStep === s;
        return (
          <React.Fragment key={s}>
            <div className={styles['step-dot-col']}>
              <div
                className={[
                  styles['step-dot'],
                  done ? styles['step-dot-done'] : active ? styles['step-dot-active'] : styles['step-dot-inactive'],
                ].join(' ')}
              >
                {done ? <Check style={{ width: 14, height: 14 }} strokeWidth={3} /> : i + 1}
              </div>
              <span
                className={[
                  styles['step-label'],
                  active || done ? styles['step-label-active'] : styles['step-label-inactive'],
                ].join(' ')}
              >
                {STEP_LABEL_MAP[s]}
              </span>
            </div>

            {i < dotsSteps.length - 1 && (
              <div
                className={[
                  styles['step-connector'],
                  currentIdx > stepIdx ? styles['step-connector-done'] : styles['step-connector-pending'],
                ].join(' ')}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Step 1: Info + Source ────────────────────────────────────────────────────

function Step1({
  eventId,
  name,
  description,
  sourceFilter,
  sourceFtpAccountIds,
  sourceIncludeManualUploads,
  onChange,
  onNext,
  onCancel,
}: {
  eventId: string;
  name: string;
  description: string;
  sourceFilter: 'all' | 'selected';
  sourceFtpAccountIds: string[];
  sourceIncludeManualUploads: boolean;
  onChange: (patch: Partial<{
    name: string;
    description: string;
    sourceFilter: 'all' | 'selected';
    sourceFtpAccountIds: string[];
    sourceIncludeManualUploads: boolean;
  }>) => void;
  onNext: () => void;
  onCancel: () => void;
}) {
  const [ftpAccounts, setFtpAccounts] = useState<FTPAccount[]>([]);
  const [loadingFtp, setLoadingFtp] = useState(false);

  useEffect(() => {
    setLoadingFtp(true);
    ftpAccountsApi
      .list(eventId)
      .then(setFtpAccounts)
      .catch(() => toast.error('Failed to load FTP accounts'))
      .finally(() => setLoadingFtp(false));
   
  }, [eventId]);

  const toggleFtp = (id: string) => {
    const next = sourceFtpAccountIds.includes(id)
      ? sourceFtpAccountIds.filter((x) => x !== id)
      : [...sourceFtpAccountIds, id];
    onChange({ sourceFtpAccountIds: next });
  };

  const canProceed = name.trim().length > 0 &&
    (sourceFilter === 'all' || sourceFtpAccountIds.length > 0 || sourceIncludeManualUploads);

  return (
    <div className={styles['step-col']}>
      <div className={styles['step-scroll']}>
        {/* Album info */}
        <div className={styles['info-fields']}>
          <Input
            label="Album Name"
            placeholder="e.g. Wedding Day Live"
            value={name}
            onChange={(e) => onChange({ name: e.target.value })}
            maxLength={80}
            required
            autoFocus
          />
          <Textarea
            label="Description (optional)"
            placeholder="A note for your guests..."
            value={description}
            onChange={(e) => onChange({ description: e.target.value })}
            rows={2}
          />
        </div>

        {/* Source config */}
        <div className={styles['source-section']}>
          <p className={styles['source-title']}>
            Photo Sources
          </p>
          <p className={styles['source-desc']}>
            Choose which incoming photos are automatically added to this live album.
          </p>

          {/* Radio: all vs selected */}
          <div className={styles['radio-group']}>
            {(['all', 'selected'] as const).map((opt) => (
              <button
                key={opt}
                onClick={() => onChange({ sourceFilter: opt })}
                className={[
                  styles['radio-btn'],
                  sourceFilter === opt ? styles['radio-btn-selected'] : '',
                ].filter(Boolean).join(' ')}
              >
                <div className={styles['radio-btn-row']}>
                  <div
                    className={[
                      styles['radio-dot'],
                      sourceFilter === opt ? styles['radio-dot-selected'] : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {sourceFilter === opt && <div className={styles['radio-dot-inner']} />}
                  </div>
                  <div>
                    <p className={styles['radio-label']}>
                      {opt === 'all' ? 'All sources' : 'Selected sources only'}
                    </p>
                    <p className={styles['radio-sublabel']}>
                      {opt === 'all'
                        ? 'All FTP accounts and manual uploads'
                        : 'Choose specific FTP accounts and/or manual uploads'}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* FTP account selector — only when "selected" */}
          {sourceFilter === 'selected' && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className={styles['source-detail-col']}
            >
              {/* Manual uploads toggle */}
              <button
                onClick={() => onChange({ sourceIncludeManualUploads: !sourceIncludeManualUploads })}
                className={[
                  styles['toggle-btn'],
                  sourceIncludeManualUploads ? styles['toggle-btn-active'] : '',
                ].filter(Boolean).join(' ')}
              >
                <div
                  className={[
                    styles['toggle-track'],
                    sourceIncludeManualUploads ? styles['toggle-track-on'] : styles['toggle-track-off'],
                  ].join(' ')}
                  style={{ width: 30, height: 18 }}
                >
                  <div
                    className={[
                      styles['toggle-thumb'],
                      sourceIncludeManualUploads ? styles['toggle-thumb-on-sm'] : styles['toggle-thumb-off'],
                    ].join(' ')}
                    style={{ width: 14, height: 14 }}
                  />
                </div>
                <span className={styles['toggle-label']}>
                  Include manual uploads
                </span>
              </button>

              {/* FTP accounts */}
              {loadingFtp ? (
                <div className={styles['skeleton-col']}>
                  {[0, 1].map((i) => (
                    <div key={i} className={styles['skeleton-item']} />
                  ))}
                </div>
              ) : ftpAccounts.length === 0 ? (
                <p className={styles['no-ftp-msg']}>
                  No FTP accounts for this event.
                </p>
              ) : (
                <div className={styles['ftp-list']}>
                  {ftpAccounts.map((acc) => {
                    const checked = sourceFtpAccountIds.includes(acc._id);
                    return (
                      <button
                        key={acc._id}
                        onClick={() => toggleFtp(acc._id)}
                        className={[
                          styles['toggle-btn'],
                          checked ? styles['toggle-btn-active'] : '',
                        ].filter(Boolean).join(' ')}
                      >
                        <div
                          className={[
                            styles['checkbox-box'],
                            checked ? styles['checkbox-box-checked'] : '',
                          ].filter(Boolean).join(' ')}
                        >
                          {checked && <Check style={{ width: 10, height: 10, color: '#fff' }} strokeWidth={3} />}
                        </div>
                        <div className={styles['ftp-info']}>
                          <p className={styles['ftp-name']}>
                            {acc.photographerName}
                          </p>
                          <p className={styles['ftp-username']}>
                            {acc.username}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      <div className={styles['step-footer']}>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={onNext} disabled={!canProceed}>
          Next
        </Button>
      </div>
    </div>
  );
}

// ─── Step Backfill: Existing photos decision ─────────────────────────────────

function StepBackfill({
  eventPhotoCount,
  backfillExisting,
  backfillRespectSourceFilter,
  sourceFilter,
  onChange,
  onBack,
  onNext,
}: {
  eventPhotoCount: number;
  backfillExisting: boolean;
  backfillRespectSourceFilter: boolean;
  sourceFilter: 'all' | 'selected';
  onChange: (patch: Partial<{ backfillExisting: boolean; backfillRespectSourceFilter: boolean }>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const options = [
    {
      value: true,
      icon: <ImageIcon style={{ width: 20, height: 20 }} />,
      title: 'Include existing photos',
      description: `Add ${eventPhotoCount} existing photo${eventPhotoCount !== 1 ? 's' : ''} to this live album`,
    },
    {
      value: false,
      icon: <Sparkles style={{ width: 20, height: 20 }} />,
      title: 'Start fresh (new photos only)',
      description: 'Album starts empty — only new uploads are added automatically',
    },
  ] as const;

  return (
    <div className={styles['step-col']}>
      <div className={styles['step-scroll']}>
        <div>
          <p className={styles['backfill-desc']}>
            This event already has <strong>{eventPhotoCount} photo{eventPhotoCount !== 1 ? 's' : ''}</strong> in
            its gallery. Would you like to include them in this live album?
          </p>
        </div>

        {/* Option cards */}
        <div className={styles['option-cards']}>
          {options.map((opt) => {
            const selected = backfillExisting === opt.value;
            return (
              <motion.button
                key={String(opt.value)}
                onClick={() => onChange({ backfillExisting: opt.value })}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.985 }}
                transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                className={[
                  styles['option-card'],
                  selected ? styles['option-card-selected'] : '',
                ].filter(Boolean).join(' ')}
              >
                <div className={styles['option-card-row']}>
                  {/* Radio dot */}
                  <div
                    className={[
                      styles['option-radio-dot'],
                      selected ? styles['option-radio-dot-selected'] : '',
                    ].filter(Boolean).join(' ')}
                    style={{ width: 18, height: 18 }}
                  >
                    {selected && <div className={styles['radio-dot-inner']} />}
                  </div>

                  {/* Icon + text */}
                  <div className={styles['option-body']}>
                    <div className={styles['option-title-row']}>
                      <span className={selected ? styles['option-icon-active'] : styles['option-icon-inactive']}>
                        {opt.icon}
                      </span>
                      <p className={styles['option-title']}>
                        {opt.title}
                      </p>
                    </div>
                    <p className={styles['option-desc']}>
                      {opt.description}
                    </p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Source filter toggle — only when backfilling AND source filter is 'selected' */}
        {backfillExisting && sourceFilter === 'selected' && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            className={styles['backfill-toggle-box']}
          >
            <button
              onClick={() => onChange({ backfillRespectSourceFilter: !backfillRespectSourceFilter })}
              className={styles['backfill-toggle-btn']}
            >
              <div
                className={[
                  styles['toggle-track'],
                  backfillRespectSourceFilter ? styles['toggle-track-on'] : styles['toggle-track-off'],
                ].join(' ')}
                style={{ width: 34, height: 20 }}
              >
                <div
                  className={[
                    styles['toggle-thumb'],
                    backfillRespectSourceFilter ? styles['toggle-thumb-on-lg'] : styles['toggle-thumb-off'],
                  ].join(' ')}
                  style={{ width: 16, height: 16 }}
                />
              </div>
              <div>
                <p className={styles['backfill-toggle-label']}>
                  Respect source filter
                </p>
                <p className={styles['backfill-toggle-sublabel']}>
                  Only include existing photos from your selected sources
                </p>
              </div>
            </button>
          </motion.div>
        )}
      </div>

      <div className={styles['step-footer']}>
        <Button variant="ghost" onClick={onBack}>
          <ChevronLeft style={{ width: 16, height: 16 }} />
          Back
        </Button>
        <Button onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
}

// ─── Step Duplicates: Duplicate handling preference ──────────────────────────

function StepDuplicates({
  duplicateReviewEnabled,
  onChange,
  onBack,
  onNext,
}: {
  duplicateReviewEnabled: boolean;
  onChange: (value: boolean) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const options = [
    {
      value: true,
      icon: <Eye style={{ width: 20, height: 20 }} />,
      title: 'Hold for review',
      description: 'Duplicate photos are kept in a review queue so you can decide which ones to add to the album.',
    },
    {
      value: false,
      icon: <Images style={{ width: 20, height: 20 }} />,
      title: 'Add to album',
      description: 'Duplicate photos are added to the album alongside the original — no photos are held back.',
    },
  ] as const;

  return (
    <div className={styles['step-col']}>
      <div className={styles['step-scroll']}>
        <p className={styles['backfill-desc']}>
          When a visually similar photo already exists in the album, what should happen to the duplicate?
        </p>

        <div className={styles['option-cards']}>
          {options.map((opt) => {
            const selected = duplicateReviewEnabled === opt.value;
            return (
              <motion.button
                key={String(opt.value)}
                onClick={() => onChange(opt.value)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.985 }}
                transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                className={[
                  styles['option-card'],
                  selected ? styles['option-card-selected'] : '',
                ].filter(Boolean).join(' ')}
              >
                <div className={styles['option-card-row']}>
                  <div
                    className={[
                      styles['option-radio-dot'],
                      selected ? styles['option-radio-dot-selected'] : '',
                    ].filter(Boolean).join(' ')}
                    style={{ width: 18, height: 18 }}
                  >
                    {selected && <div className={styles['radio-dot-inner']} />}
                  </div>
                  <div className={styles['option-body']}>
                    <div className={styles['option-title-row']}>
                      <span className={selected ? styles['option-icon-active'] : styles['option-icon-inactive']}>
                        {opt.icon}
                      </span>
                      <p className={styles['option-title']}>{opt.title}</p>
                    </div>
                    <p className={styles['option-desc']}>{opt.description}</p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className={styles['step-footer']}>
        <Button variant="ghost" onClick={onBack}>
          <ChevronLeft style={{ width: 16, height: 16 }} />
          Back
        </Button>
        <Button onClick={onNext}>Next</Button>
      </div>
    </div>
  );
}

// ─── Step 2: Overlays ─────────────────────────────────────────────────────────

function Step2({
  eventId,
  overlayConfigs,
  submitting,
  onAdd,
  onUpdate,
  onRemove,
  onBack,
  onNext,
  isOwner,
  attachDefaultOverlay,
  onToggleAttachDefault,
  defaultBranded,
  defaultAnchor,
}: {
  eventId: string;
  overlayConfigs: AlbumOverlayConfig[];
  submitting: boolean;
  onAdd: (overlay: Overlay) => void;
  onUpdate: (index: number, updated: AlbumOverlayConfig) => void;
  onRemove: (index: number) => void;
  onBack: () => void;
  onNext: () => void;
  isOwner: boolean;
  attachDefaultOverlay: boolean;
  onToggleAttachDefault: (value: boolean) => void;
  defaultBranded: boolean;
  defaultAnchor: OverlayAnchor | null;
}) {
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [loadingOverlays, setLoadingOverlays] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;
    setLoadingOverlays(true);
    overlaysApi
      .list(eventId)
      .then((data) => { setOverlays(data); setInitialized(true); })
      .catch(() => toast.error('Failed to load overlays'))
      .finally(() => setLoadingOverlays(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const addedOverlayIds = new Set(overlayConfigs.map((c) => c.overlayId));
  const availableToAdd = overlays.filter((o) => !addedOverlayIds.has(o._id));

  return (
    <div className={styles['step-col']}>
      <div className={styles['step-scroll-shorter']}>
        <p className={styles['overlay-hint']}>
          Overlays are applied in real-time to each photo as it arrives.
          Skip this step if you want no overlay.
        </p>

        {/* Add overlay strip */}
        {loadingOverlays ? (
          <div className={styles['overlay-skeleton-row']}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={styles['overlay-skeleton-item']} />
            ))}
          </div>
        ) : overlays.length === 0 ? (
          <div className={styles['overlay-empty-msg']}>
            No overlays uploaded for this event. Add overlays in the Overlays tab first.
          </div>
        ) : availableToAdd.length > 0 ? (
          <div>
            <p className={styles['overlay-add-label']}>Add Overlay</p>
            <div className={styles['overlay-strip']}>
              {availableToAdd.map((overlay) => (
                <button
                  key={overlay._id}
                  onClick={() => onAdd(overlay)}
                  aria-label={`Add overlay ${overlay.name}`}
                  className={styles['overlay-add-btn']}
                >
                  <div className={styles['overlay-add-thumb']}>
                    <img
                      src={overlaysApi.fileUrl(overlay._id)}
                      alt={overlay.name}
                      className={styles['img-contain']}
                    />
                    <div className={styles['overlay-add-hover-mask']}>
                      <Plus style={{ width: 16, height: 16, color: 'var(--clay-orange)' }} />
                    </div>
                  </div>
                  <span className={styles['overlay-add-name']}>
                    {overlay.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Configured overlays */}
        {overlayConfigs.length === 0 ? (
          <div className={styles['overlay-none-msg']}>
            No overlays added — photos will be shown without overlays
          </div>
        ) : (
          <div className={styles['overlay-configs-col']}>
            {overlayConfigs.map((config, i) => {
              const overlay = overlays.find((o) => o._id === config.overlayId);
              if (!overlay) return null;
              const reservedAnchors = getReservedAnchors(overlayConfigs, i, {
                defaultBranded,
                defaultAnchor: defaultAnchor ?? undefined,
              });
              return (
                <OverlayConfigCard
                  key={config.overlayId}
                  overlay={overlay}
                  config={config}
                  reservedAnchors={reservedAnchors}
                  onChange={(updated) => onUpdate(i, updated)}
                  onRemove={() => onRemove(i)}
                />
              );
            })}
          </div>
        )}

        {/* Watermark disclaimer / owner opt-in checkbox */}
        {isOwner ? (
          <label className={styles['watermark-box']} style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={attachDefaultOverlay}
              onChange={(e) => onToggleAttachDefault(e.target.checked)}
              style={{ marginTop: '0.125rem', accentColor: 'var(--clay-orange)' }}
              aria-label="Attach default Pixel Machha branding watermark"
            />
            <p className={styles['watermark-text']}>
              Attach the <strong>default Pixel Machha branding watermark</strong> to every photo in this live album.
              As an owner you can untick this to skip the default branding for this album only.
            </p>
          </label>
        ) : (
          <div className={styles['watermark-box']}>
            <span className={styles['watermark-icon']}>&#9888;</span>
            <p className={styles['watermark-text']}>
              A <strong>Pixel Machha watermark</strong> is automatically applied to all album photos during beta.
            </p>
          </div>
        )}
      </div>

      <div className={styles['step-footer']}>
        <Button variant="ghost" onClick={onBack} disabled={submitting}>
          <ChevronLeft style={{ width: 16, height: 16 }} />
          Back
        </Button>
        <Button onClick={onNext} loading={submitting}>
          Create Live Album
        </Button>
      </div>
    </div>
  );
}

// ─── Venue Display panel (shown on Done step) ────────────────────────────────

function VenueDisplayPanel({
  album,
  onAlbumUpdated,
}: {
  album: Album;
  onAlbumUpdated: (updated: Album) => void;
}) {
  const [setupOpen, setSetupOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const isEnabled = album.slideshowEnabled ?? false;
  const slideshowUrl = album.slideshowToken
    ? `${window.location.origin}/slideshow/${album._id}/${album.slideshowToken}`
    : null;

  const handleCopy = useCallback(async () => {
    if (!slideshowUrl) return;
    try {
      await navigator.clipboard.writeText(slideshowUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Slideshow URL copied!');
    } catch {
      toast.error('Failed to copy');
    }
  }, [slideshowUrl]);

  return (
    <>
      <div className={styles['venue-panel']}>
        <div className={styles['venue-panel-header']}>
          <div className={styles['venue-panel-icon']}>
            <MonitorPlay style={{ width: 16, height: 16 }} />
          </div>
          <div className={styles['venue-panel-title-col']}>
            <p className={styles['venue-panel-title']}>Venue Display</p>
            <p className={styles['venue-panel-sub']}>
              {isEnabled
                ? 'Open this URL on a TV or projector at your venue.'
                : 'Show photos in real-time on a TV or projector at your venue.'}
            </p>
          </div>
        </div>

        {isEnabled && slideshowUrl ? (
          <div className={styles['venue-panel-body']}>
            <div className={styles['venue-url-row']}>
              <span className={styles['venue-url-text']}>{slideshowUrl}</span>
              <button
                type="button"
                onClick={handleCopy}
                className={styles['venue-url-icon-btn']}
                aria-label="Copy slideshow URL"
              >
                {copied ? <Check style={{ width: 14, height: 14 }} /> : <Copy style={{ width: 14, height: 14 }} />}
              </button>
              <a
                href={slideshowUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles['venue-url-icon-btn']}
                aria-label="Open slideshow in new tab"
              >
                <ExternalLink style={{ width: 14, height: 14 }} />
              </a>
            </div>
            <button
              type="button"
              onClick={() => setSetupOpen(true)}
              className={styles['venue-manage-link']}
            >
              Manage settings & password
            </button>
          </div>
        ) : (
          <div className={styles['venue-panel-body']}>
            <Button size="sm" onClick={() => setSetupOpen(true)}>
              <MonitorPlay style={{ width: 14, height: 14 }} />
              Set up Venue Display
            </Button>
          </div>
        )}
      </div>

      <SlideshowSetupModal
        album={setupOpen ? album : null}
        isOpen={setupOpen}
        onClose={() => setSetupOpen(false)}
        onUpdated={onAlbumUpdated}
      />
    </>
  );
}

// ─── QR Code display ──────────────────────────────────────────────────────────

function QRCodeDisplay({
  inviteToken,
  onOpenPoster,
}: {
  inviteToken: string;
  onOpenPoster?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const inviteUrl = `${getPortalUrl('viewer')}/join/${inviteToken}`;
    QRCode.toCanvas(canvasRef.current, inviteUrl, {
      width: 220,
      margin: 2,
      color: { dark: '#1a0000', light: '#ffffff' },
      errorCorrectionLevel: 'M',
    });
  }, [inviteToken]);

  function handleDownload() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'invite-qr-code.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  return (
    <div className={styles['qr-display']}>
      <div className={styles['qr-canvas-wrap']}>
        <canvas ref={canvasRef} />
      </div>
      <div className={styles['qr-actions']}>
        <Button variant="secondary" size="sm" onClick={handleDownload}>
          <Download style={{ width: 14, height: 14 }} />
          Download QR Code
        </Button>
        {onOpenPoster && (
          <Button variant="secondary" size="sm" onClick={onOpenPoster}>
            <FileImage style={{ width: 14, height: 14 }} />
            Download Poster
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Step 3: Done — invite link ───────────────────────────────────────────────

function Step3Done({
  album,
  eventName,
  onAlbumUpdated,
  onDone,
}: {
  album: Album;
  eventName: string;
  onAlbumUpdated: (updated: Album) => void;
  onDone: () => void;
}) {
  const [showPoster, setShowPoster] = useState(false);

  return (
    <div className={styles['done-col']}>
      {/* Success header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 26 }}
        className={styles['done-header']}
      >
        <div className={styles['done-icon-circle']}>
          <Zap style={{ width: 28, height: 28, color: 'var(--clay-orange)' }} strokeWidth={1.5} />
        </div>
        <div>
          <div className={styles['done-title-row']}>
            <p className={styles['done-title']}>
              {album.name}
            </p>
            <LiveBadge active />
          </div>
          <p className={styles['done-subtitle']}>
            Your live album is ready. Share the invite link with your guests.
          </p>
        </div>
      </motion.div>

      {/* QR Code */}
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15, duration: 0.3 }}
      >
        <QRCodeDisplay
          inviteToken={album.inviteToken}
          onOpenPoster={() => setShowPoster(true)}
        />
      </motion.div>

      <A4PosterModal
        isOpen={showPoster}
        onClose={() => setShowPoster(false)}
        albumId={album._id}
        albumName={album.name}
        eventName={eventName}
        inviteToken={album.inviteToken}
      />

      {/* Live indicator badge */}
      <div className={styles['live-info-box']}>
        <Wifi style={{ width: 16, height: 16 }} className={styles['live-info-icon']} />
        <p className={styles['live-info-text']}>
          Photos will appear automatically as they arrive from configured sources. Guests joining now will see them in real-time.
        </p>
      </div>

      {/* Invite link */}
      <InviteLinkPanel album={album} onTokenRegenerated={onAlbumUpdated} />

      {/* Venue Display (slideshow) — high-visibility entry point */}
      <VenueDisplayPanel album={album} onAlbumUpdated={onAlbumUpdated} />

      <div className={styles['step-footer-end']}>
        <Button onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

export function LiveAlbumWizard({ isOpen, onClose, eventId, onCreated }: LiveAlbumWizardProps) {
  const [step, setStep] = useState<LogicalStep>('source');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'selected'>('all');
  const [sourceFtpAccountIds, setSourceFtpAccountIds] = useState<string[]>([]);
  const [sourceIncludeManualUploads, setSourceIncludeManualUploads] = useState(true);
  const [backfillExisting, setBackfillExisting] = useState(false);
  const [backfillRespectSourceFilter, setBackfillRespectSourceFilter] = useState(true);
  const [faceConfig, setFaceConfig] = useState<FaceRecognitionConfig>({
    faceRecognitionEnabled: false,
    guestVisibility: 'all',
    faceSearchScope: 'album',
  });
  const [duplicateReviewEnabled, setDuplicateReviewEnabled] = useState(true);
  const [overlayConfigs, setOverlayConfigs] = useState<AlbumOverlayConfig[]>([]);
  // Owner-only opt-in for the system default branding watermark.
  // Pre-checked: owners get the watermark by default but can untick to skip
  // it for an individual album. Non-owners always receive it server-side.
  const [attachDefaultOverlay, setAttachDefaultOverlay] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [createdAlbum, setCreatedAlbum] = useState<Album | null>(null);
  const [eventPhotoCount, setEventPhotoCount] = useState<number>(0);
  const [eventName, setEventName] = useState('');
  const [defaultAnchor, setDefaultAnchor] = useState<OverlayAnchor | null>(null);

  const { quotaModal, handleQuotaError, closeQuotaModal } = useQuotaGuard();
  const isOwner = useAuthStore((s) => s.user?.isOwner ?? false);

  // Fetch the system default branding overlay anchor for the reserved-anchor logic
  useEffect(() => {
    if (!isOpen || defaultAnchor) return;
    brandingApi.getDefaults()
      .then((d) => setDefaultAnchor(d.config.anchor))
      .catch(() => {/* non-critical — disabled-anchor logic just won't kick in */});
  }, [isOpen, defaultAnchor]);

  // System watermark will be applied to this album?
  const defaultBranded = !isOwner || attachDefaultOverlay;

  // Whether the backfill step should be shown
  const showBackfillStep = eventPhotoCount > 0;

  // Build the step sequence dynamically
  const steps = useMemo<LogicalStep[]>(() => {
    const s: LogicalStep[] = ['source'];
    if (showBackfillStep) s.push('backfill');
    s.push('face', 'duplicates', 'overlays', 'done');
    return s;
  }, [showBackfillStep]);

  const nextStep = (current: LogicalStep): LogicalStep => {
    const idx = steps.indexOf(current);
    return steps[Math.min(idx + 1, steps.length - 1)];
  };

  const prevStep = (current: LogicalStep): LogicalStep => {
    const idx = steps.indexOf(current);
    return steps[Math.max(idx - 1, 0)];
  };

  // Fetch event photo count on open
  useEffect(() => {
    if (isOpen && eventId) {
      eventsApi
        .get(eventId)
        .then((event) => {
          setEventPhotoCount(event.photoCount ?? 0);
          setEventName(event.name ?? '');
        })
        .catch(() => setEventPhotoCount(0));
    }
   
  }, [isOpen, eventId]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep('source');
      setName('');
      setDescription('');
      setSourceFilter('all');
      setSourceFtpAccountIds([]);
      setSourceIncludeManualUploads(true);
      setBackfillExisting(false);
      setBackfillRespectSourceFilter(true);
      setFaceConfig({ faceRecognitionEnabled: false, guestVisibility: 'all', faceSearchScope: 'album' });
      setDuplicateReviewEnabled(true);
      setOverlayConfigs([]);
      setAttachDefaultOverlay(true);
      setSubmitting(false);
      setCreatedAlbum(null);
    }
  }, [isOpen]);

  const patchSource = (patch: Partial<{
    name: string;
    description: string;
    sourceFilter: 'all' | 'selected';
    sourceFtpAccountIds: string[];
    sourceIncludeManualUploads: boolean;
  }>) => {
    if (patch.name !== undefined) setName(patch.name);
    if (patch.description !== undefined) setDescription(patch.description);
    if (patch.sourceFilter !== undefined) setSourceFilter(patch.sourceFilter);
    if (patch.sourceFtpAccountIds !== undefined) setSourceFtpAccountIds(patch.sourceFtpAccountIds);
    if (patch.sourceIncludeManualUploads !== undefined) setSourceIncludeManualUploads(patch.sourceIncludeManualUploads);
  };

  const patchBackfill = (patch: Partial<{
    backfillExisting: boolean;
    backfillRespectSourceFilter: boolean;
  }>) => {
    if (patch.backfillExisting !== undefined) setBackfillExisting(patch.backfillExisting);
    if (patch.backfillRespectSourceFilter !== undefined) setBackfillRespectSourceFilter(patch.backfillRespectSourceFilter);
  };

  const handleAddOverlay = (overlay: Overlay) => {
    setOverlayConfigs((prev) => {
      const reserved = getReservedAnchors(prev, -1, {
        defaultBranded,
        defaultAnchor: defaultAnchor ?? undefined,
      });
      const anchor = pickFreeAnchor(reserved);
      return [
        ...prev,
        { overlayId: overlay._id, anchor, size: 'md', opacity: 1 },
      ];
    });
  };

  const handleUpdateOverlay = (index: number, updated: AlbumOverlayConfig) => {
    setOverlayConfigs((prev) => prev.map((c, i) => (i === index ? updated : c)));
  };

  const handleRemoveOverlay = (index: number) => {
    setOverlayConfigs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const album = await albumsApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        eventId,
        isLive: true,
        sourceFilter,
        sourceFtpAccountIds: sourceFilter === 'selected' ? sourceFtpAccountIds : undefined,
        sourceIncludeManualUploads: sourceFilter === 'selected' ? sourceIncludeManualUploads : undefined,
        backfillExisting: showBackfillStep ? backfillExisting : undefined,
        backfillRespectSourceFilter: showBackfillStep && backfillExisting ? backfillRespectSourceFilter : undefined,
        // Owner opt-in. Non-owners always get the default server-side regardless.
        ...(isOwner && { attachDefaultOverlay }),
        // Face recognition
        faceRecognitionEnabled: faceConfig.faceRecognitionEnabled,
        ...(faceConfig.faceRecognitionEnabled && {
          guestVisibility: faceConfig.guestVisibility,
        }),
        // Duplicate handling
        duplicateReviewEnabled,
      });
      setCreatedAlbum(album);
      onCreated(album);
      setStep('done');
    } catch (err) {
      if (!handleQuotaError(err)) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(msg || 'Failed to create live album');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const titleMap: Record<LogicalStep, string> = {
    source: 'New Live Album',
    backfill: 'Existing Photos',
    face: 'Face Recognition',
    duplicates: 'Duplicate Photos',
    overlays: 'Overlays',
    done: 'Live Album Ready',
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={titleMap[step]} size="md">
        <div className={styles['wizard-body']}>
          {step !== 'done' && <StepDots currentStep={step} steps={steps} />}

          {step === 'source' && (
            <Step1
              eventId={eventId}
              name={name}
              description={description}
              sourceFilter={sourceFilter}
              sourceFtpAccountIds={sourceFtpAccountIds}
              sourceIncludeManualUploads={sourceIncludeManualUploads}
              onChange={patchSource}
              onNext={() => setStep(nextStep('source'))}
              onCancel={onClose}
            />
          )}

          {step === 'backfill' && (
            <StepBackfill
              eventPhotoCount={eventPhotoCount}
              backfillExisting={backfillExisting}
              backfillRespectSourceFilter={backfillRespectSourceFilter}
              sourceFilter={sourceFilter}
              onChange={patchBackfill}
              onBack={() => setStep(prevStep('backfill'))}
              onNext={() => setStep(nextStep('backfill'))}
            />
          )}

          {step === 'face' && (
            <FaceRecognitionStep
              config={faceConfig}
              onChange={(patch) => setFaceConfig((prev) => ({ ...prev, ...patch }))}
              isLive={true}
              onNext={() => setStep(nextStep('face'))}
              onBack={() => setStep(prevStep('face'))}
            />
          )}

          {step === 'duplicates' && (
            <StepDuplicates
              duplicateReviewEnabled={duplicateReviewEnabled}
              onChange={setDuplicateReviewEnabled}
              onBack={() => setStep(prevStep('duplicates'))}
              onNext={() => setStep(nextStep('duplicates'))}
            />
          )}

          {step === 'overlays' && (
            <Step2
              eventId={eventId}
              overlayConfigs={overlayConfigs}
              submitting={submitting}
              onAdd={handleAddOverlay}
              onUpdate={handleUpdateOverlay}
              onRemove={handleRemoveOverlay}
              onBack={() => setStep(prevStep('overlays'))}
              onNext={handleCreate}
              isOwner={isOwner}
              attachDefaultOverlay={attachDefaultOverlay}
              onToggleAttachDefault={setAttachDefaultOverlay}
              defaultBranded={defaultBranded}
              defaultAnchor={defaultAnchor}
            />
          )}

          {step === 'done' && createdAlbum && (
            <Step3Done
              album={createdAlbum}
              eventName={eventName}
              onAlbumUpdated={(updated) => setCreatedAlbum(updated)}
              onDone={onClose}
            />
          )}
        </div>
      </Modal>

      <QuotaLimitModal
        {...quotaModal}
        eventId={eventId}
        onClose={closeQuotaModal}
      />
    </>
  );
}
